import React, { useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import {
  loginUser,
  registerUser,
  sendPasswordResetEmail,
} from "../../services/authService";
import { LoginProps } from "../../types";
import { usePasswordValidation } from "../../hooks/usePasswordValidation";
import PasswordValidation from "../Common/PasswordValidation";
import {
  Eye,
  EyeOff,
  User,
  Shield,
  Package,
  Calendar,
  Users,
  TrendingUp,
} from "lucide-react";
import { getFuncionarioByCode } from "../../services/funcionarioService";
import { Funcionario } from "../../types";
import "./Login.css";

interface LoginPropsWithReset extends LoginProps {
  onShowResetPassword?: () => void;
  onShowFuncionarioLogin?: () => void;
  onFuncionarioLoginSuccess?: (funcionario: Funcionario) => void;
}

type UserType = "admin" | "funcionario";

const LoginModern: React.FC<LoginPropsWithReset> = ({
  onLoginSuccess,
  onShowResetPassword,
  onShowFuncionarioLogin,
  onFuncionarioLoginSuccess,
}) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [userType, setUserType] = useState<UserType>("admin");
  const [codigo, setCodigo] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [resetLoading, setResetLoading] = useState<boolean>(false);
  const [resetMessage, setResetMessage] = useState<string>("");
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false,
  });

  const { isPasswordValid } = usePasswordValidation(formData.password);
  const passwordsMatch = formData.password === formData.confirmPassword;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const togglePasswordVisibility = (field: "password" | "confirmPassword") => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Se for funcionário, fazer login por código
    if (userType === "funcionario") {
      if (!codigo.trim()) {
        setError("Código de acesso é obrigatório");
        setLoading(false);
        return;
      }

      try {
        const result = await getFuncionarioByCode(codigo.trim());

        if (result.success && result.data) {
          if (onFuncionarioLoginSuccess) {
            onFuncionarioLoginSuccess(result.data);
          }
        } else {
          setError("Código de acesso inválido ou funcionário inativo");
        }
      } catch (err) {
        console.error("❌ Erro ao fazer login:", err);
        setError("Erro ao fazer login. Tente novamente.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Login de admin
    const result = await loginUser(formData.email, formData.password);

    if (result.success && result.data) {
      onLoginSuccess(result.data);
    } else {
      setError(result.error || "Erro ao fazer login");
    }

    setLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!isPasswordValid) {
      setError("A senha não atende aos critérios de segurança");
      setLoading(false);
      return;
    }

    if (!passwordsMatch) {
      setError("As senhas não coincidem");
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError("A senha deve ter pelo menos 8 caracteres");
      setLoading(false);
      return;
    }

    const result = await registerUser(
      formData.email,
      formData.password,
      formData.name,
    );

    if (result.success && result.data) {
      onLoginSuccess(result.data);
    } else {
      setError(result.error || "Erro ao criar conta");
    }

    setLoading(false);
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    setError("");
    setFormData({
      email: "",
      password: "",
      confirmPassword: "",
      name: "",
    });
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setResetMessage("");
    setError("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      setError("Por favor, digite um email válido");
      setResetLoading(false);
      return;
    }

    try {
      const result = await sendPasswordResetEmail(resetEmail);

      if (result.success) {
        setResetMessage(
          "Instruções de recuperação enviadas para seu email! Verifique sua caixa de entrada e spam.",
        );
        setResetEmail("");
      } else {
        setError(result.error || "Erro ao enviar email de recuperação");
      }
    } catch (error) {
      setError("Erro ao enviar email de recuperação");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="login-modern-container">
      {/* Left Side - Hero Section */}
      <div className="login-hero-section">
        <div className="hero-content">
          <div className="hero-brand">
            <h1 className="brand-title">
              <span className="brand-system">System</span>
              <span className="brand-loc">Loc</span>
            </h1>
            <p className="brand-tagline">
              Elegância na gestão da sua loja. Simplifique o controle de
              locações e inventário com tecnologia de ponta.
            </p>
          </div>

          <div className="hero-image-container">
            <img
              src="/images/hero-login.jpg"
              alt="Profissional gerenciando loja"
              className="hero-image"
              onError={(e) => {
                // Fallback para imagem padrão se a imagem não existir
                e.currentTarget.style.display = "none";
              }}
            />
          </div>

          <div className="features-list">
            <h3 className="features-title">
              Todas as funcionalidades que você precisa
            </h3>

            <div className="feature-item">
              <div className="feature-icon">
                <Package size={20} />
              </div>
              <div className="feature-info">
                <h4>Gestão de Estoque</h4>
                <p>Controle completo do seu inventário em tempo real</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <Calendar size={20} />
              </div>
              <div className="feature-info">
                <h4>Controle de Atendimentos</h4>
                <p>Gerencie todos os atendimentos e locações ativas</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <Calendar size={20} />
              </div>
              <div className="feature-info">
                <h4>Cadastro de Reservas</h4>
                <p>Sistema completo de agendamento e reservas</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <Users size={20} />
              </div>
              <div className="feature-info">
                <h4>Cadastro de Clientes</h4>
                <p>Base de dados completa dos seus clientes</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <User size={20} />
              </div>
              <div className="feature-info">
                <h4>Controle de Funcionários</h4>
                <p>Gestão de equipe com permissões personalizadas</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <TrendingUp size={20} />
              </div>
              <div className="feature-info">
                <h4>Relatórios Avançados</h4>
                <p>Análises detalhadas para decisões inteligentes</p>
              </div>
            </div>
          </div>

          <div className="hero-footer">
            <p>Teste grátis por 14 dias • Sem cartão de crédito</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="login-form-section">
        <div className="login-card-modern">
          <div className="login-card-header">
            <h2 className="welcome-title">
              {showResetPassword ? "Recuperar Senha" : "Bem-vindo de volta"}
            </h2>
            <p className="welcome-subtitle">
              {showResetPassword
                ? "Digite seu email para receber instruções de recuperação de senha"
                : "Entre com suas credenciais para acessar o sistema"}
            </p>
          </div>

          {/* User Type Tabs */}
          {!showResetPassword && (
            <div className="user-type-tabs">
              <button
                className={`tab-button ${userType === "admin" ? "active" : ""}`}
                onClick={() => {
                  setUserType("admin");
                  setError("");
                  setCodigo("");
                }}
              >
                <Shield size={18} />
                <span>Admin</span>
              </button>
              <button
                className={`tab-button ${userType === "funcionario" ? "active" : ""}`}
                onClick={() => {
                  setUserType("funcionario");
                  setError("");
                  setFormData({
                    email: "",
                    password: "",
                    confirmPassword: "",
                    name: "",
                  });
                }}
              >
                <User size={18} />
                <span>Funcionário</span>
              </button>
            </div>
          )}

          {error && (
            <Alert variant="danger" className="modern-alert">
              {error}
            </Alert>
          )}
          {resetMessage && (
            <Alert variant="success" className="modern-alert">
              {resetMessage}
            </Alert>
          )}

          {/* Formulário de Reset de Senha */}
          {showResetPassword ? (
            <Form onSubmit={handleResetPassword} className="modern-form">
              <Form.Group className="modern-form-group">
                <Form.Label className="modern-label">Email</Form.Label>
                <Form.Control
                  data-cy="reset-email-input"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  placeholder="seu@email.com"
                  className="modern-input"
                  disabled={resetLoading}
                />
              </Form.Group>

              <Button
                type="submit"
                className="modern-submit-button"
                disabled={resetLoading}
              >
                {resetLoading ? "Enviando..." : "Enviar instruções"}
              </Button>

              <div className="text-center mt-3">
                <button
                  type="button"
                  className="footer-link"
                  onClick={() => {
                    setShowResetPassword(false);
                    setResetEmail("");
                    setResetMessage("");
                    setError("");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  ← Voltar para o login
                </button>
              </div>
            </Form>
          ) : (
            <Form
              onSubmit={isLogin ? handleLogin : handleRegister}
              className="modern-form"
            >
              {/* Inputs para Funcionário */}
              {userType === "funcionario" && isLogin && (
                <Form.Group className="modern-form-group">
                  <Form.Label className="modern-label">
                    Código de Acesso
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={codigo}
                    data-cy="code-input"
                    onChange={(e) => setCodigo(e.target.value.toUpperCase())}
                    required
                    disabled={loading}
                    placeholder="VEN-1234"
                    className="modern-input"
                    style={{
                      textAlign: "center",
                      fontSize: "18px",
                      letterSpacing: "2px",
                      fontFamily: "monospace",
                      fontWeight: "600",
                    }}
                  />
                  <Form.Text
                    className="text-muted"
                    style={{
                      display: "block",
                      textAlign: "center",
                      marginTop: "8px",
                    }}
                  >
                    Digite o código fornecido pelo administrador
                  </Form.Text>
                </Form.Group>
              )}

              {/* Inputs para Admin */}
              {userType === "admin" && (
                <>
                  {!isLogin && (
                    <Form.Group className="modern-form-group">
                      <Form.Label className="modern-label">
                        Nome Completo
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        data-cy="name-input"
                        value={formData.name}
                        onChange={handleChange}
                        required={!isLogin}
                        placeholder="Seu nome completo"
                        className="modern-input"
                      />
                    </Form.Group>
                  )}

                  <Form.Group className="modern-form-group">
                    <Form.Label className="modern-label">Email</Form.Label>
                    <Form.Control
                      type="email"
                      data-cy="email-input"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="admin@systemloc.com"
                      className="modern-input"
                    />
                  </Form.Group>
                </>
              )}

              {/* Senha apenas para Admin */}
              {userType === "admin" && (
                <Form.Group className="modern-form-group">
                  <Form.Label className="modern-label">Senha</Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type={showPasswords.password ? "text" : "password"}
                      name="password"
                      data-cy="password-input"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      placeholder="••••••••"
                      className={`modern-input ${!isLogin && formData.password && !isPasswordValid ? "is-invalid" : ""}`}
                    />
                    <Button
                      type="button"
                      variant="link"
                      className="password-toggle-modern"
                      onClick={() => togglePasswordVisibility("password")}
                    >
                      {showPasswords.password ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </Button>
                  </div>
                  {!isLogin && (
                    <PasswordValidation password={formData.password} />
                  )}
                </Form.Group>
              )}

              {!isLogin && userType === "admin" && (
                <Form.Group className="modern-form-group">
                  <Form.Label className="modern-label">
                    Confirmar Senha
                  </Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type={showPasswords.confirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      data-cy="confirm-password-input"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required={!isLogin}
                      placeholder="Confirme sua senha"
                      className={`modern-input ${formData.confirmPassword && !passwordsMatch ? "is-invalid" : ""}`}
                    />
                    <Button
                      type="button"
                      variant="link"
                      className="password-toggle-modern"
                      onClick={() =>
                        togglePasswordVisibility("confirmPassword")
                      }
                    >
                      {showPasswords.confirmPassword ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </Button>
                  </div>
                  {formData.confirmPassword && passwordsMatch && (
                    <div className="password-match-indicator">
                      <span>✓ Senhas coincidem</span>
                    </div>
                  )}
                </Form.Group>
              )}

              <Button
                type="submit"
                className="modern-submit-button"
                disabled={
                  loading ||
                  (!isLogin &&
                    userType === "admin" &&
                    (!isPasswordValid || !passwordsMatch))
                }
              >
                {loading
                  ? isLogin
                    ? "Entrando..."
                    : "Criando conta..."
                  : isLogin
                    ? `Entrar como ${userType === "admin" ? "Admin" : "Funcionário"}`
                    : "Cadastrar"}
              </Button>
            </Form>
          )}

          {!showResetPassword && (
            <div className="login-card-footer">
              <p className="footer-text">
                {userType === "funcionario" && isLogin
                  ? "Acesso ao sistema de cadastro de medidas"
                  : "Acesso completo ao sistema de gestão"}
              </p>

              {userType === "admin" && (
                <>
                  {isLogin ? (
                    <div className="footer-links">
                      <button className="footer-link"
                        // onClick={switchMode}
                        onClick={() => {}}
                      >
                        Não tem uma conta?{" "}
                        <span className="link-highlight">Criar conta</span>
                      </button>
                      <span className="separator">•</span>
                      <button
                        className="footer-link"
                        onClick={() => setShowResetPassword(true)}
                      >
                        <span className="link-highlight">
                          Esqueceu a senha?
                        </span>
                      </button>
                    </div>
                  ) : (
                    <div className="footer-links">
                      <button className="footer-link" onClick={switchMode}>
                        Já tem uma conta?{" "}
                        <span className="link-highlight">Fazer login</span>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModern;
